// CopyButton + CopyableValue — copy-to-clipboard, decided once (added
// 2026-09-18, audit C-14, batch 14). Eight consumer sites in two modules had
// three feedback behaviours (a glyph swap for 1500 ms, a toast.success, or
// nothing at all), two error policies (an optional-chained fire-and-forget that
// swallowed a denied clipboard, and an await + catch), and the `IdRow` helper
// pasted between AuthorDetail and PublisherDetail differing by one prop, with
// fin's `CopyableValue` a third copy. Now:
//
//   copyToClipboard   the one write: navigator.clipboard where the context is
//                     secure, the legacy execCommand path where it is not (an
//                     http LAN dev box, or a denied permission), and a
//                     toast.error when both fail — a copy that did not happen
//                     is NEVER silent, because the operator's next act is to
//                     paste it somewhere.
//   CopyButton        an IconButton whose glyph swaps to a Check for 1500 ms
//                     on success (`feedback: 'swap'`, the default), or also
//                     raises a toast.success (`'toast'` — for a value the user
//                     cannot see change, a URL in a dialog). The label is the
//                     tooltip and the aria-label ("Copy ISBN"); an sr-only
//                     status announces the confirmation. It stops propagation:
//                     a copy is never a row activation.
//   CopyableValue     the VALUE half of a definition: the text (mono by
//                     default — these are identifiers) with the button trailing,
//                     optionally rendered as an external link. It carries no
//                     label of its own on purpose — it sits in a DefinitionGrid
//                     item's `value` slot or a <dd>, and the <dt> prints the
//                     label; `label` here only names the verb ("Copy ISNI").
//                     The button's 32px box rides a negative block margin so a
//                     definition with a copy button is no taller than one
//                     without (the Tag remove-glyph idiom: a 20px line is not
//                     a 20px target).
import { Show, createSignal, onCleanup, type Accessor } from 'solid-js';
import { Check, Copy, ExternalLink } from 'lucide-solid';
import { IconButton, type IconButtonSize, type TooltipPlacement } from '../icon-button/index.js';
import { toast } from '../toast/index.js';

/** `swap` (default): the glyph becomes a Check for 1500 ms. `toast`: that AND a toast.success — for a value the user cannot see change (a URL in a dialog). Failure is a toast.error in both. */
export type CopyFeedback = 'swap' | 'toast';

/** How long the Check glyph (and the sr-only "Copied") stay — the sources' own 1500 ms, kept. */
export const COPIED_MS = 1500;

/** The failure line — one sentence, the same on every screen. */
export const COPY_FAILED_MESSAGE = "Couldn't copy — select the text and copy it manually.";

/** Where the copy failed for real, or a test forces it: exported so a demo knob and a test can reach the failure path without breaking the clipboard. */
export const clipboardWriter = { write: writeClipboard };

async function writeClipboard(text: string): Promise<boolean> {
  // The modern API exists only in a SECURE context (https, localhost) and can
  // still reject on a denied permission — both fall through to the legacy path.
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  // The legacy path: a scratch textarea off-screen, `execCommand('copy')`.
  // Deprecated in the spec, still the only route on an http LAN dev box.
  if (typeof document === 'undefined') return false;
  const scratch = document.createElement('textarea');
  try {
    scratch.value = text;
    scratch.setAttribute('readonly', '');
    scratch.setAttribute('aria-hidden', 'true');
    scratch.style.position = 'fixed';
    scratch.style.left = '-9999px';
    document.body.appendChild(scratch);
    scratch.select();
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    // In `finally`, not after the command: a browser with no execCommand at
    // all throws before the remove, and the scratch would leak into the DOM.
    scratch.remove();
  }
}

/**
 * The one clipboard write. Resolves `true` on success. `feedback: 'toast'`
 * raises a toast.success naming the value; a failure ALWAYS raises a
 * toast.error — never silent. `label` is the value's name ("ISBN", "Pairing
 * link"), used in the toast copy.
 */
export async function copyToClipboard(o: { text: string; label?: string; feedback?: CopyFeedback }): Promise<boolean> {
  const ok = await clipboardWriter.write(o.text);
  if (!ok) toast.error(COPY_FAILED_MESSAGE);
  else if (o.feedback === 'toast') toast.success(`${o.label ?? 'Value'} copied to clipboard.`);
  return ok;
}

export interface CopyButtonProps {
  /** What goes to the clipboard — a string or an accessor (a pairing URL that regenerates). */
  text: string | Accessor<string>;
  /** The value's NAME ("ISBN", "Pairing link"): the button is named "Copy <label>" and the toast says "<label> copied". Default "to clipboard" → "Copy to clipboard". */
  label?: string;
  /** `swap` (default) or `toast` — see the type. */
  feedback?: CopyFeedback;
  /** IconButton's glyph rung: `s` (default) beside a value, `m` in a toolbar. */
  size?: IconButtonSize;
  /** Tooltip side (default `top`). */
  placement?: TooltipPlacement;
  /** Automation id on the <button> — REQUIRED: every copy site is an automation target (`copy-isbn-${id}`). */
  id: string;
  /** Inert — the value is not there yet. */
  disabled?: boolean;
  /** Layout classes on the outer element (see IconButton). */
  class?: string;
}

/**
 * An IconButton that copies `text`. Success swaps the glyph to a Check for
 * 1500 ms (and, with `feedback: 'toast'`, raises a toast); failure raises a
 * toast.error. Stops click propagation — a copy never activates the row.
 */
export function CopyButton(props: CopyButtonProps) {
  const [copied, setCopied] = createSignal(false);
  let timer: ReturnType<typeof setTimeout> | undefined;
  onCleanup(() => clearTimeout(timer));
  const text = () => (typeof props.text === 'function' ? props.text() : props.text);
  const label = () => props.label ?? 'to clipboard';
  const copy = async () => {
    const ok = await copyToClipboard({ text: text(), label: props.label, feedback: props.feedback });
    if (!ok) return;
    setCopied(true);
    clearTimeout(timer);
    timer = setTimeout(() => setCopied(false), COPIED_MS);
  };
  return (
    <>
      <IconButton
        id={props.id}
        icon={copied() ? Check : Copy}
        label={copied() ? 'Copied' : `Copy ${label()}`}
        size={props.size}
        placement={props.placement}
        disabled={props.disabled}
        class={props.class}
        onClick={(e) => {
          e.stopPropagation();
          void copy();
        }}
      />
      {/* The swap is visual; this is the same confirmation for a screen reader. */}
      <span class="sr-only" role="status">
        {copied() ? 'Copied to clipboard' : ''}
      </span>
    </>
  );
}

export interface CopyableValueProps {
  /** The value's NAME — names the copy verb ("Copy ISNI") and the toast. NOT printed: the surrounding <dt> or DefinitionGrid label does that. */
  label: string;
  /** The value shown and copied. */
  value: string;
  /** Monospace (default true — identifiers, codes, account numbers). `false` for prose-like values. */
  mono?: boolean;
  /** Automation id on the copy <button>. */
  id: string;
  /** Render the value as an external link (target _blank, noreferrer) with the ExternalLink glyph — an ORCID, a registry lookup. */
  href?: string;
  /** Passed to the CopyButton (default `swap`). */
  feedback?: CopyFeedback;
}

/**
 * A definition's VALUE with a copy button trailing — for a DefinitionGrid
 * item's `value` slot or a <dd>. Mono by default; `href` makes the value an
 * external link. Its height is the text line: the button's 32px box rides a
 * negative block margin so copyable and plain definitions sit level.
 */
export function CopyableValue(props: CopyableValueProps) {
  const mono = () => props.mono ?? true;
  return (
    <span class="inline-flex min-w-0 max-w-full items-center gap-1">
      <Show
        when={props.href}
        fallback={<span class={`min-w-0 truncate ${mono() ? 'font-mono' : ''}`}>{props.value}</span>}
      >
        {(href) => (
          <a
            href={href()}
            target="_blank"
            rel="noreferrer"
            class={`link link-hover inline-flex min-w-0 items-center gap-1 truncate text-primary ${mono() ? 'font-mono' : ''}`}
          >
            {props.value}
            <ExternalLink size={12} aria-hidden="true" />
          </a>
        )}
      </Show>
      <CopyButton id={props.id} text={props.value} label={props.label} feedback={props.feedback} class="-my-1.5" />
    </span>
  );
}
